import { isEmpty } from 'lodash';

import { hasRunEnded } from '~/components/ClaimPage/CliveApp/conductorStatusObservation';

import {
  getDisposition,
  getInResponseTo,
  getMessageId,
  getRunId,
  getRunReply,
  getSentAt,
  isRunEnded,
} from '../../conversation/metadata';
import type { LogConversation, RunReply } from '../../conversation/types';
import { getNotifiedAskId } from '../../utils';
import type { LogEntryModel, MessageRef } from '../types';

type QuestionEntryModel = Extract<LogEntryModel, { kind: 'question' }>;
type HandlerTurnEntryModel = Extract<LogEntryModel, { kind: 'handler_turn' }>;
type EndingEntryModel = Extract<LogEntryModel, { kind: 'run_footer' }>['ending'];

/** The kinds a run's ending is drawn as: a log, an escalation's accent, or the card it waits on. */
const isEndingKind = (entry: LogEntryModel): entry is EndingEntryModel =>
  entry.kind === 'action' || entry.kind === 'accent' || entry.kind === 'log';

const toMessageRef = ({ notification }: HandlerTurnEntryModel): MessageRef => ({
  eventId: notification.event_id,
  sentAt: getSentAt(notification) ?? notification.timestamp,
  author: notification.title || null,
});

/**
 * The log's entries as a conversation with Clive, from the stream contract's keys alone: a run's ending that
 * carries a reply becomes its run's footer, a question Clive raised in reply is held until the ending that
 * names it, and the handler's message and the reply to it link to each other.
 *
 * A question raised in reply is drawn inside the first ending whose reply names it. Until one does, it is
 * held while its run may still name it, and is drawn where it was raised once the run is over without
 * having named it: an ending of the run's is in the log, the status says so, or a later run has published.
 * Until the status is read, only the log can say the run is over. A reply naming a question the log holds no
 * entry for draws what it has, and an ending without a reply is drawn as it was.
 *
 * `entries` are the stream's, joined, one entry per ask, and in stream order.
 */
export const buildConversation = (entries: LogEntryModel[], { status }: LogConversation): LogEntryModel[] => {
  const turnsByMessageId = new Map<string, HandlerTurnEntryModel>();
  const replyQuestionsByAskId = new Map<string, QuestionEntryModel>();
  const endedRunIds = new Set<string>();
  entries.forEach((entry) => {
    const { notification } = entry;
    if (entry.kind === 'handler_turn') {
      const messageId = getMessageId(notification);
      if (messageId && !turnsByMessageId.has(messageId)) turnsByMessageId.set(messageId, entry);
    }

    if (entry.kind === 'question' && !isEmpty(getInResponseTo(notification))) {
      const askId = getNotifiedAskId(notification);
      if (askId) replyQuestionsByAskId.set(askId, entry);
    }

    const runId = getRunId(notification);
    if (runId && isRunEnded(notification)) endedRunIds.add(runId);
  });

  const replyFor = (entry: LogEntryModel): RunReply | null =>
    isEndingKind(entry) && isRunEnded(entry.notification) ? getRunReply(entry.notification) : null;

  // The first ending to name a question draws it, and the first to answer a message is the one it links to.
  const endingByAskId = new Map<string, string>();
  const endingByMessageId = new Map<string, string>();
  entries.forEach((entry) => {
    const reply = replyFor(entry);
    const eventId = entry.notification.event_id;
    reply?.askIds.forEach((askId) => {
      if (replyQuestionsByAskId.has(askId) && !endingByAskId.has(askId)) endingByAskId.set(askId, eventId);
    });
    reply?.messageIds.forEach((messageId) => {
      if (!endingByMessageId.has(messageId)) endingByMessageId.set(messageId, eventId);
    });
  });

  const turnsFor = (messageIds: string[]): HandlerTurnEntryModel[] =>
    messageIds.flatMap((messageId) => turnsByMessageId.get(messageId) ?? []);

  const withLink = (question: QuestionEntryModel): QuestionEntryModel => {
    const [turn] = turnsFor(getInResponseTo(question.notification));

    return { ...question, inResponseTo: turn ? toMessageRef(turn) : null };
  };

  const isRunOver = (runId: string | null, index: number): boolean =>
    hasRunEnded(status, runId) ||
    (runId !== null && endedRunIds.has(runId)) ||
    entries.slice(index + 1).some(({ notification }) => {
      const laterRunId = getRunId(notification);

      return laterRunId !== null && laterRunId !== runId;
    });

  return entries.flatMap((entry, index): LogEntryModel[] => {
    const { notification } = entry;

    if (entry.kind === 'question') {
      const askId = getNotifiedAskId(notification);
      if (!askId || replyQuestionsByAskId.get(askId) !== entry) return [entry];
      if (endingByAskId.has(askId)) return [];

      return isRunOver(getRunId(notification), index) ? [withLink(entry)] : [];
    }

    if (entry.kind === 'handler_turn') {
      const messageId = getMessageId(notification);
      const replyEventId = messageId ? endingByMessageId.get(messageId) : undefined;

      return [replyEventId ? { ...entry, replyEventId } : entry];
    }

    const reply = replyFor(entry);
    if (!reply || !isEndingKind(entry)) return [entry];

    return [
      {
        kind: 'run_footer',
        notification,
        ending: entry,
        disposition: getDisposition(notification),
        reply,
        foldedQuestions: reply.askIds.flatMap((askId) => {
          const question = replyQuestionsByAskId.get(askId);

          return question && endingByAskId.get(askId) === notification.event_id ? [withLink(question)] : [];
        }),
        messages: turnsFor(reply.messageIds).map(toMessageRef),
      },
    ];
  });
};
